// src/features/chat/components/Composer.jsx

import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image as ImageIcon, ArrowUp, X } from 'lucide-react';
import ImageUploader from './ImageUploader';
import { useImageUploads } from '../hooks/useImageUploads';
import { useIsMobileLike } from '../../../hooks/useIsMobileLike';
import styles from '../styles/Composer.module.css';

const MAX_IMAGES = 6;

/**
 * Isolated chat composer component.
 *
 * Key features:
 * - Isolated state: typing doesn't re-render message list
 * - Image upload with preview
 * - Desktop: Enter sends, Shift+Enter newline
 * - Mobile-like: Enter inserts newline (send button only)
 *
 * @param {Object} props
 * @param {Function} props.onSend - Callback with { text, files, primitiveSelectionId }
 * @param {boolean} props.disabled - Whether input is disabled
 * @param {boolean} props.sending - Whether currently sending
 * @param {string} [props.placeholder] - Input placeholder text
 * @param {Object|null} [props.seedMessage] - Message text and optional selection token to place in the composer
 * @param {Function} [props.topSlot] - Render prop for the composer zone above
 *   the input; receives { isEmpty }. Composer state stays isolated.
 */
export function Composer({
  onSend,
  disabled,
  sending,
  placeholder,
  seedMessage = null,
  topSlot = null,
}) {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  const [primitiveSelectionId, setPrimitiveSelectionId] = useState(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const isMobileLike = useIsMobileLike();
  const {
    images,
    addFiles,
    removeImage,
    canAddMore,
    prepareForSend,
    commitSend,
    rollbackSend,
  } = useImageUploads({ maxImages: MAX_IMAGES });

  // Derived states for button visibility and enablement
  const hasContent = useMemo(
    () => text.trim().length > 0 || images.length > 0,
    [text, images.length]
  );
  const canSend = useMemo(
    () => hasContent && !disabled && !sending,
    [hasContent, disabled, sending]
  );
  const showSendButton = useMemo(
    () => hasContent || sending,
    [hasContent, sending]
  );

  // Keep textarea height in sync with content and reset when cleared
  const adjustTextareaHeight = useCallback((value) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = 'auto';
    if (!value) {
      textarea.style.height = '';
      return;
    }
    textarea.style.height = `${Math.min(textarea.scrollHeight, 300)}px`;
  }, []);

  useEffect(() => {
    adjustTextareaHeight(text);
  }, [text, adjustTextareaHeight]);

  useEffect(() => {
    if (!seedMessage || typeof seedMessage.text !== 'string') return;

    setText(seedMessage.text);
    setPrimitiveSelectionId(seedMessage.primitiveSelectionId || null);
    textareaRef.current?.focus();
  }, [seedMessage]);

  // Handle form submit
  const handleSubmit = useCallback(
    async (e) => {
      e?.preventDefault();
      if (!canSend) return;

      const currentText = text;
      const currentPrimitiveSelectionId = primitiveSelectionId || undefined;
      const batch = prepareForSend();
      setText('');
      setPrimitiveSelectionId(null);
      adjustTextareaHeight('');

      try {
        await onSend({
          text: currentText,
          files: batch.map((entry) => entry.file),
          primitiveSelectionId: currentPrimitiveSelectionId,
        });
        commitSend(batch);
      } catch (err) {
        setText(currentText);
        setPrimitiveSelectionId(currentPrimitiveSelectionId || null);
        rollbackSend(batch);
        adjustTextareaHeight(currentText);
        console.error('Error sending message:', err);
      }
    },
    [
      canSend,
      text,
      primitiveSelectionId,
      onSend,
      prepareForSend,
      commitSend,
      rollbackSend,
      adjustTextareaHeight,
    ]
  );

  // Handle key down (Desktop: Enter sends, Shift+Enter newline. Mobile-like: Enter newline.)
  const handleKeyDown = useCallback(
    (e) => {
      if (isMobileLike) return;
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit, isMobileLike]
  );

  // Handle text change with auto-resize
  const handleTextChange = useCallback((e) => {
    const nextValue = e.target.value;
    setText(nextValue);
    if (primitiveSelectionId && !nextValue.trim()) {
      setPrimitiveSelectionId(null);
    }
    adjustTextareaHeight(nextValue);
  }, [adjustTextareaHeight, primitiveSelectionId]);

  const handleImageButtonClick = useCallback(() => {
    if (disabled || sending || !canAddMore) return;
    fileInputRef.current?.click();
  }, [disabled, sending, canAddMore]);

  return (
    <div className={styles.container}>
      {/* Composer-zone slot (e.g. starter suggestions) */}
      {typeof topSlot === 'function' && topSlot({ isEmpty: !hasContent })}

      {/* Image upload preview */}
      {images.length > 0 && (
        <div className={styles.uploadedImages}>
          {images.map((img) => (
            <div key={img.id} className={styles.uploadedImageContainer}>
              <img
                src={img.preview}
                alt=""
                className={styles.uploadedImage}
              />
              <button
                type="button"
                className={styles.removeImageButton}
                onClick={() => removeImage(img.id)}
                aria-label={t('chat.remove_image', 'Remove image')}
              >
                <X size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      <form className={styles.form} onSubmit={handleSubmit}>
        {/* Image upload button */}
        <button
          type="button"
          className={styles.uploadButton}
          onClick={handleImageButtonClick}
          aria-label={t('chat.upload_images')}
          disabled={disabled || sending || !canAddMore}
          title={
            !canAddMore
              ? t('chat.max_images_reached', { max: MAX_IMAGES })
              : t('chat.upload_images')
          }
        >
          <ImageIcon size={22} />
        </button>

        {/* Text input */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || t('chat.placeholder', 'Ask me anything...')}
          disabled={disabled || sending}
          rows={1}
          className={styles.textarea}
          aria-label={t('chat.input_aria', 'Message input')}
        />

        {/* Send button */}
        <button
          type="submit"
          className={[
            styles.sendButton,
            showSendButton ? styles.sendButtonVisible : '',
            sending ? styles.sendButtonLoading : '',
          ]
            .filter(Boolean)
            .join(' ')}
          disabled={!canSend}
          aria-label={
            sending ? t('chat.processing_message') : t('chat.send_message')
          }
        >
          {sending ? (
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
              className={styles.sendSpinner}
              width={20}
              height={20}
            >
              <circle cx="12" cy="12" r="9" />
            </svg>
          ) : (
            <ArrowUp size={20} />
          )}
        </button>

        {/* Hidden file input for image uploading */}
        <ImageUploader
          onFilesSelected={addFiles}
          fileInputRef={fileInputRef}
        />
      </form>
    </div>
  );
}

export default Composer;
